import type { FC, ReactNode } from 'react'

import Link from 'next/link'

import { useCallback, useEffect, useState } from 'react'
import { Cookie } from 'lucide-react'

import { Button } from '@/components/generals/button'
import { cn } from '@/lib/utils'

import {
    CONSENT_REOPEN_EVENT,
    CONSENT_SIGNALS,
    CONSENT_STORAGE_KEY
} from '@/constants/analytics'

type Choice = 'granted' | 'denied'

const readStoredChoice = (): Choice | null => {
    try {
        const stored = window.localStorage.getItem(CONSENT_STORAGE_KEY)

        return stored === 'granted' || stored === 'denied' ? stored : null
    } catch {
        return null
    }
}

export const CookieConsent: FC = (): ReactNode => {
    const [mounted, setMounted] = useState<boolean>(false)
    const [open, setOpen] = useState<boolean>(false)

    const openBanner = useCallback((): void => {
        setMounted(true)
        // Next frame, so the element paints in its hidden state first and the
        // transition to the visible state actually animates.
        window.requestAnimationFrame(() => setOpen(true))
    }, [])

    useEffect(() => {
        if (!readStoredChoice()) openBanner()

        window.addEventListener(CONSENT_REOPEN_EVENT, openBanner)

        return () => window.removeEventListener(CONSENT_REOPEN_EVENT, openBanner)
    }, [openBanner])

    const choose = useCallback((choice: Choice): void => {
        try {
            window.localStorage.setItem(CONSENT_STORAGE_KEY, choice)
        } catch {
            // Private mode or storage disabled: the choice applies to this
            // page view only, and the banner asks again next visit.
        }

        const update = Object.fromEntries(
            CONSENT_SIGNALS.map((signal) => [signal, choice] as const)
        )

        window.gtag?.('consent', 'update', update)
        window.dataLayer?.push({ event: 'consent_update', consent_state: choice })

        setOpen(false)
        window.setTimeout(() => setMounted(false), 300)
    }, [])

    if (!mounted) return null

    return (
        <div
            role='dialog'
            aria-label='Cookie consent'
            aria-live='polite'
            className={cn(
                'fixed inset-x-0 bottom-0 z-50 p-4 transition-all duration-300 ease-out motion-reduce:transition-none sm:p-6',
                open ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
            )}
        >
            <div className='mx-auto flex max-w-3xl flex-col gap-4 rounded-2xl border border-border bg-card p-5 shadow-lg sm:flex-row sm:items-center sm:gap-6'>
                <Cookie className='size-6 shrink-0 text-primary' aria-hidden='true' />

                <p className='flex-1 text-sm leading-relaxed text-muted-foreground'>
                    We use cookies to understand how the site is used and to improve it.
                    You can accept them or continue with only the essentials.{' '}
                    <Link
                        href='/privacy'
                        className='font-medium text-primary underline underline-offset-4 hover:no-underline'
                    >
                        Privacy Policy
                    </Link>
                </p>

                <div className='flex shrink-0 gap-3'>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => choose('denied')}
                    >
                        Decline
                    </Button>

                    <Button
                        variant='primary'
                        size='sm'
                        onClick={() => choose('granted')}
                    >
                        Accept
                    </Button>
                </div>
            </div>
        </div>
    )
}
